import { useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import UsersManagement from './UsersManagement';
import SecuritySettings from './SecuritySettings';
import ConfigurationSettings from './ConfigurationSettings';
import { useAuth } from '../../context/AuthContext';
import ContactMessages from './ContactMessages';

const { FiUsers, FiShield, FiSettings, FiHome, FiMail } = FiIcons;

const AdminPanel = () => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname.split('/admin/')[1] || 'users';

    useEffect(() => {
		if (!user) {
			navigate('/signin', { replace: true });
		}
	}, [user, navigate]);

    const tabs = [
        { id: 'users', name: 'Users Management', icon: FiUsers, path: '/admin/users' },
        { id: 'contacts', name: 'Contact Messages', icon: FiMail, path: '/admin/contacts' },
        { id: 'security', name: 'Security', icon: FiShield, path: '/admin/security' },
        { id: 'configuration', name: 'Configuration', icon: FiSettings, path: '/admin/configuration' }
    ];

    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="flex items-center space-x-4 mb-6">
                        <Link
                            to="/"
                            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-emerald-600 transition-colors"
                        >
                            <SafeIcon icon={FiHome} className="w-4 h-4" />
                            <span>Back to Home</span>
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                        Admin Panel
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-300">
                        Manage users, security settings, and platform configuration.
                    </p>
                </motion.div>

                {/* Tabs Navigation */}
                <div className="mb-8">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => {
                                const isActive = currentPath === tab.id || (currentPath === '' && tab.id === 'users');

                                return (
                                    <Link
                                        key={tab.id}
                                        to={tab.path}
                                        className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${isActive
                                                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                                            }`}
                                    >
                                        <SafeIcon icon={tab.icon} className="w-4 h-4" />
                                        <span>{tab.name}</span>
                                    </Link>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Routes>
                        <Route path="/" element={<UsersManagement />} />
                        <Route path="/users" element={<UsersManagement />} />
                        <Route path="/contacts" element={<ContactMessages />} />
                        <Route path="/security" element={<SecuritySettings />} />
                        <Route path="/configuration" element={<ConfigurationSettings />} />
                    </Routes>
                </motion.div>
            </div>
        </div>
    );
};

export default AdminPanel;